import { CommonModule } from "@angular/common";
import { Component, ElementRef, OnDestroy, OnInit, ViewChild, computed, signal, ChangeDetectionStrategy } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { DxPopoverModule, DxTextBoxModule, DxTextBoxComponent } from "devextreme-angular";
import { Subject, Subscription, from, merge, of } from "rxjs";
import { catchError, debounceTime, filter, map, switchMap, tap } from "rxjs/operators";
import { GpdButtonComponent } from "../gpd-button/gpd-button.component";
import { GpdPillComponent } from "../gpd-pill/gpd-pill.component";
import { SvgIconComponent } from "../svg-icon/svg-icon.component";
import { StatusPillComponent } from "../status-pill/status-pill.component";
import { UserAvatarComponent } from "../user-avatar/user-avatar.component";
import { SharedPipesModule } from "src/app/pipes/shared.pipes.module";
import { AppGlobals } from "src/app/app.globals";
import { SearchService } from "src/app/services/search.service";
import { FileViewerService } from "src/app/services/file-viewer.service";
import { GlobalSearchResultItem, GlobalSearchResultModel } from "src/app/interfaces/entities/search";
import {
  enabledGroupMeta, GroupMeta, mergeSharePointResults, normalizeCounts, parkSearch,
  routeForResult, searchCacheKey, SearchScope, statusConfigFor, wavesForScope,
} from "./search-shared";

export interface ScopeOption {
  key: SearchScope;
  label: string;
}

interface ResultGroup extends GroupMeta {
  items: GlobalSearchResultItem[];
  total: number;
  extra: number; // how many more matches exist beyond the shown items
}

const MIN_SEARCH_LENGTH = 2;
const DEBOUNCE_MS = 250;
const TAKE_PER_TYPE = 5;
// A scoped search has far fewer matches to begin with, so fetch more of them per type —
// the popover still renders only ROWS_PER_GROUP of them.
const SCOPED_TAKE_PER_TYPE = 25;
// Rows rendered per group in the dropdown, regardless of how many were fetched.
const ROWS_PER_GROUP = 5;
const RECENT_SEARCHES_KEY = "wi_recent_searches";
const MAX_RECENT = 5;

@Component({
    selector: "app-global-search",
    templateUrl: "./global-search.component.html",
    styleUrls: ["./global-search.component.css"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CommonModule, DxPopoverModule, DxTextBoxModule, SvgIconComponent, StatusPillComponent, SharedPipesModule, UserAvatarComponent, GpdPillComponent, GpdButtonComponent]
})
export class GlobalSearchComponent implements OnInit, OnDestroy {
  // Exposed for the inline result rows in the template.
  statusConfigFor = statusConfigFor;
  readonly searchPlaceholder = $localize`:@@searchPlaceholder:Global Search…`;
  readonly searchInputAttr = { autocomplete: 'off', 'aria-label': $localize`:@@searchPlaceholder:Global Search…` };
  @ViewChild("searchTarget", { static: true }) searchTarget!: ElementRef<HTMLElement>;
  @ViewChild("searchInput") searchInput?: DxTextBoxComponent;

  query = signal<string>("");
  loading = signal<boolean>(false);
  loadingSharePoint = signal<boolean>(false);
  // Waves still in flight after the first one painted — drives the same "still searching"
  // hint as SharePoint, so the dropdown never looks finished while results are still coming.
  pendingWaves = signal<number>(0);
  hasSearched = signal<boolean>(false);
  popoverVisible = signal<boolean>(false);
  activeIndex = signal<number>(-1);
  recentSearches = signal<string[]>([]);

  // Scope: IDs extracted from the current URL; scope selection drives filtered search.
  currentSiteId = signal<number | null>(null);
  currentProjectId = signal<number | null>(null);
  searchScope = signal<SearchScope>("global");

  availableScopes = computed<ScopeOption[]>(() => {
    const scopes: ScopeOption[] = [{ key: "global", label: "Alle" }];
    if (this.currentSiteId()) scopes.push({ key: "site", label: "Dieser Standort" });
    if (this.currentProjectId()) scopes.push({ key: "project", label: "Dieses Projekt" });
    return scopes;
  });

  private rawResults = signal<GlobalSearchResultItem[]>([]);
  private counts = signal<{ [entityType: string]: number }>({});
  // SharePoint totals live apart from the DB counts: both land asynchronously, and folding
  // them into one signal let whichever arrived second silently erase the other's numbers.
  private spCounts = signal<{ [entityType: string]: number }>({});

  // DB counts plus SharePoint counts, which cover different rows of the same two groups.
  private displayCounts = computed<{ [entityType: string]: number }>(() => {
    const merged = { ...this.counts() };
    const sp = this.spCounts();
    for (const type of Object.keys(sp)) merged[type] = (merged[type] ?? 0) + sp[type];
    return merged;
  });

  // Grouped, in the defined display order; empty groups are dropped.
  groups = computed<ResultGroup[]>(() => {
    const items = this.rawResults();
    const counts = this.displayCounts();
    return enabledGroupMeta().map((meta) => {
      const matched = items.filter((r) => r.entityType === meta.type);
      // A scoped search fetches generously but the popover is 400px wide — show a fixed
      // handful and let `extra` carry the rest to the results page.
      const groupItems = matched.slice(0, ROWS_PER_GROUP);
      const total = Math.max(counts[meta.type] ?? 0, matched.length);
      return { ...meta, items: groupItems, total, extra: Math.max(0, total - groupItems.length) };
    }).filter((g) => g.items.length > 0);
  });

  // Flat list mirroring visual order, for keyboard navigation.
  flatItems = computed<GlobalSearchResultItem[]>(() => this.groups().flatMap((g) => g.items));

  private searchSubject = new Subject<string>();
  private subscription?: Subscription;
  private routerSub?: Subscription;
  // The term the currently shown results were loaded for (to detect stale state, e.g. after navigation).
  private loadedFor = "";
  private spToken = 0;
  private destroyed = false;

  constructor(
    private appGlobals: AppGlobals,
    private searchService: SearchService,
    private router: Router,
    private fileViewerService: FileViewerService
  ) {}

  ngOnInit(): void {
    this.recentSearches.set(this.loadRecentSearches());
    this.updateContextFromUrl(this.router.url);
    this.routerSub = this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe((e) => this.updateContextFromUrl((e as NavigationEnd).urlAfterRedirects));

    this.subscription = this.searchSubject
      .pipe(
        debounceTime(DEBOUNCE_MS),
        tap((term) => {
          // A new term invalidates everything on screen; waves append onto a clean slate.
          this.loading.set(true);
          this.rawResults.set([]);
          this.counts.set({});
          this.spCounts.set({});
          this.activeIndex.set(-1);
          this.loadedFor = term;
        }),
        switchMap((term) => {
          const scoped = this.searchScope() !== "global";
          const take = scoped ? SCOPED_TAKE_PER_TYPE : TAKE_PER_TYPE;
          const waves = wavesForScope(this.searchScope());
          this.pendingWaves.set(waves.length);
          // Every wave is one request and lands independently, so the cheap types show up
          // without waiting on the task hierarchy. SharePoint starts now too.
          this.fireSharePointSearch(term);
          return merge(
            ...waves.map((types) =>
              from(this.searchService.globalSearch({ search: term, take, types, ...this.scopeContext() })).pipe(
                map((result) => ({ term, types, result })),
                catchError((err) => {
                  // Otherwise a failed wave is indistinguishable from "no matches of this type".
                  console.error('[search wave]', types, err);
                  return of({ term, types, result: null as GlobalSearchResultModel | null });
                })
              )
            )
          );
        })
      )
      .subscribe(({ term, types, result }) => {
        // A wave from a superseded term (or an unsubscribed scope switch) must not paint.
        if (this.loadedFor !== term) return;
        this.pendingWaves.update((n) => Math.max(0, n - 1));
        // Scope is applied server-side only: the client re-filter that used to run here
        // dropped every type without a projectId (documents, contacts, contracts…), which
        // is what made "Dieses Projekt" come back empty.
        this.rawResults.update((prev) => [...prev, ...(result?.results ?? [])]);
        this.counts.update((prev) => ({ ...prev, ...normalizeCounts(result?.counts, types) }));
        this.loading.set(false);
        this.hasSearched.set(true);
        // Deliberately not reopening the popover here: a slow wave landing after the user
        // pressed Escape or clicked a result would pop it back open over the new page.
        this.park(term);
      });
  }

  // Marks whatever is in flight as stale. switchMap drops the observables, so their
  // handlers never run and never decrement pendingWaves — leaving the "loading more" hint
  // pinned forever. Every place that clears loadedFor has to come through here.
  private invalidateInFlight(): void {
    this.loadedFor = "";
    this.pendingWaves.set(0);
    // Same argument as pendingWaves: navigating away mid-search invalidates the waves
    // without starting new ones, so nothing would ever clear the "Searching…" state.
    this.loading.set(false);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.subscription?.unsubscribe();
    this.routerSub?.unsubscribe();
  }

  setScope(scope: SearchScope): void {
    if (this.searchScope() === scope) return;
    this.searchScope.set(scope);
    this.invalidateInFlight();
    const term = this.query().trim();
    if (term.length >= MIN_SEARCH_LENGTH) {
      this.loading.set(true);
      this.searchSubject.next(term);
    }
  }

  onInput(value: string): void {
    this.query.set(value);
    const term = value.trim();
    if (term.length < MIN_SEARCH_LENGTH) {
      this.rawResults.set([]);
      this.counts.set({});
      this.hasSearched.set(false);
      this.loading.set(false);
      this.spCounts.set({});
      this.invalidateInFlight();
      this.popoverVisible.set(this.recentSearches().length > 0);
      return;
    }
    this.popoverVisible.set(true);
    this.searchSubject.next(term);
  }

  onFocus(): void {
    const term = this.query().trim();
    if (term.length < MIN_SEARCH_LENGTH) {
      if (this.recentSearches().length > 0) {
        this.popoverVisible.set(true);
      }
      return;
    }
    this.popoverVisible.set(true);
    // Results stale (e.g. after navigating to /search and coming back) → re-run the search.
    if (this.loadedFor !== term) {
      this.searchSubject.next(term);
    }
  }

  onKeydown(event: KeyboardEvent): void {
    const items = this.flatItems();
    switch (event.key) {
      case "ArrowDown":
        if (!items.length) return;
        event.preventDefault();
        this.popoverVisible.set(true);
        this.activeIndex.set((this.activeIndex() + 1) % items.length);
        break;
      case "ArrowUp":
        if (!items.length) return;
        event.preventDefault();
        this.activeIndex.set((this.activeIndex() - 1 + items.length) % items.length);
        break;
      case "Enter": {
        const idx = this.activeIndex();
        if (idx >= 0 && idx < items.length) {
          event.preventDefault();
          this.navigateTo(items[idx]);
        } else if (this.query().trim().length >= MIN_SEARCH_LENGTH) {
          // No row selected → go to the full results page for the current query.
          event.preventDefault();
          this.showAll();
        }
        break;
      }
      case "Escape":
        this.close();
        break;
    }
  }

  isActive(item: GlobalSearchResultItem): boolean {
    const idx = this.activeIndex();
    return idx >= 0 && this.flatItems()[idx] === item;
  }

  contextDisplay(item: GlobalSearchResultItem): string {
    return item.context ?? '';
  }

  navigateTo(item: GlobalSearchResultItem, event?: MouseEvent): void {
    this.saveRecentSearch(this.query().trim());
    if (item.entityType === "Document" || item.entityType === "SandboxDocument") {
      this.fileViewerService.openOrDownloadFile(
        item.fileType ?? "",
        item.fileOpenIdStr ?? String(item.fileOpenId ?? ""),
        item.title,
        // project-document downloads resolve by folder + filename, not by id.
        item.fileUrl ?? ""
      );
      this.close();
      return;
    }
    const target = routeForResult(item);
    if (target) {
      let queryParams = target.queryParams ?? {};
      if (item.entityType === "Spark") {
        queryParams = { ...queryParams, q: this.query() };
      }
      if (event?.ctrlKey || event?.metaKey) {
        const urlTree = this.router.createUrlTree(
          target.commands as any[],
          Object.keys(queryParams).length ? { queryParams } : {}
        );
        window.open(this.router.serializeUrl(urlTree), "_blank");
        return;
      }
      this.router.navigate(target.commands, Object.keys(queryParams).length ? { queryParams } : {});
    }
    this.close();
  }

  showMoreLabel(group: ResultGroup): string {
    return $localize`:@@searchShowMore:+ ${group.extra}:extra: more ${group.label}:label: →`;
  }

  // "+N weitere {Label}" → full results page filtered to this type.
  showMore(group: ResultGroup): void {
    this.saveRecentSearch(this.query().trim());
    this.router.navigate(["/search"], { queryParams: { q: this.query().trim(), type: group.type, ...this.navScopeContext() } });
    this.close();
  }

  // "Alle Ergebnisse anzeigen" → full results page, all types.
  showAll(): void {
    const term = this.query().trim();
    if (term.length < MIN_SEARCH_LENGTH) return;
    this.saveRecentSearch(term);
    this.router.navigate(["/search"], { queryParams: { q: term, ...this.navScopeContext() } });
    this.close();
  }

  applyRecentSearch(term: string): void {
    this.query.set(term);
    this.loading.set(true);
    this.searchSubject.next(term);
    this.searchInput?.instance.focus();
  }

  removeRecentSearch(term: string, event: Event): void {
    event.stopPropagation();
    const updated = this.recentSearches().filter((s) => s !== term);
    this.recentSearches.set(updated);
    this.persistRecentSearches(updated);
    if (updated.length === 0 && !this.hasSearched()) {
      this.popoverVisible.set(false);
    }
  }

  clearRecentSearches(): void {
    this.recentSearches.set([]);
    this.persistRecentSearches([]);
    if (!this.hasSearched()) {
      this.popoverVisible.set(false);
    }
  }

  private updateContextFromUrl(url: string): void {
    const match = url.match(/\/site\/(\d+)(?:\/project\/(\d+))?/);
    const newSiteId = match ? +match[1] : null;
    const newProjectId = match?.[2] ? +match[2] : null;
    // If context changes while a scoped search is active, mark results as stale.
    if ((newSiteId !== this.currentSiteId() || newProjectId !== this.currentProjectId()) && this.searchScope() !== "global") {
      this.invalidateInFlight();
    }
    this.currentSiteId.set(newSiteId);
    this.currentProjectId.set(newProjectId);
    // Downgrade scope if context no longer supports it.
    const scope = this.searchScope();
    if (scope === "project" && !newProjectId) this.searchScope.set(newSiteId ? "site" : "global");
    if (scope === "site" && !newSiteId) this.searchScope.set("global");
  }

  // Returns the siteId/projectId filter to pass to the API.
  private scopeContext(): Record<string, number> {
    switch (this.searchScope()) {
      case "project": return this.currentProjectId() ? { projectId: this.currentProjectId()! } : {};
      case "site": return this.currentSiteId() ? { siteId: this.currentSiteId()! } : {};
      default: return {};
    }
  }

  // Full scope params for navigation — includes both IDs and a human-readable label so the
  // results page can display which project/site is being searched.
  private navScopeContext(): Record<string, string | number> {
    const ctx: Record<string, string | number> = {};
    if (this.searchScope() === "site" || this.searchScope() === "project") {
      if (this.currentSiteId()) ctx["siteId"] = this.currentSiteId()!;
    }
    if (this.searchScope() === "project") {
      if (this.currentProjectId()) ctx["projectId"] = this.currentProjectId()!;
      ctx["scopeLabel"] = this.appGlobals.$currentProjectName.getValue() || "diesem Projekt";
    } else if (this.searchScope() === "site") {
      ctx["scopeLabel"] = this.appGlobals.$currentSiteName.getValue() || "diesem Standort";
    }
    return ctx;
  }


  // Hands the current result set to the results page so "Alle Ergebnisse anzeigen" paints
  // without re-running the search. Keyed exactly as the results page reads its URL params.
  private park(term: string): void {
    const scope = this.searchScope();
    const siteId = scope !== "global" ? this.currentSiteId() : null;
    const projectId = scope === "project" ? this.currentProjectId() : null;
    parkSearch(searchCacheKey(term, siteId, projectId), this.rawResults(), this.displayCounts());
  }

  private saveRecentSearch(term: string): void {
    if (term.length < MIN_SEARCH_LENGTH) return;
    const current = this.recentSearches().filter((s) => s !== term);
    const updated = [term, ...current].slice(0, MAX_RECENT);
    this.recentSearches.set(updated);
    this.persistRecentSearches(updated);
  }

  private loadRecentSearches(): string[] {
    try {
      const raw = localStorage.getItem(RECENT_SEARCHES_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private persistRecentSearches(searches: string[]): void {
    try {
      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(searches));
    } catch {}
  }

  private fireSharePointSearch(term: string): void {
    const token = ++this.spToken;
    this.loadingSharePoint.set(true);
    const scoped = this.searchScope() !== "global";
    const payload = {
      search: term,
      take: scoped ? SCOPED_TAKE_PER_TYPE : TAKE_PER_TYPE,
      ...this.scopeContext(),
    };
    this.searchService.searchSharePoint(payload)
      .then(spResult => {
        if (this.destroyed) return;
        // Ignore stale responses (e.g. if the user typed again before this resolved).
        if (token !== this.spToken || this.loadedFor !== term) return;
        // SP results are already scope-filtered server-side (projectId/siteId sent in payload).
        const spItems = spResult?.results ?? [];
        const prev = this.rawResults();
        const merged = mergeSharePointResults(prev, spItems);
        this.rawResults.set(merged);

        // A file tracked in the DB *and* found in SharePoint is one row after
        // mergeSharePointResults, but one in each total. Subtract what it actually dropped
        // so the group header cannot advertise documents that were deduplicated away.
        const countOf = (rows: GlobalSearchResultItem[], t: string) => rows.filter((r) => r.entityType === t).length;
        const dedupedAway = (t: string) => countOf(spItems, t) - (countOf(merged, t) - countOf(prev, t));

        // Written to their own signal, so a DB wave landing later cannot erase them.
        const spRaw = spResult?.counts ?? {};
        this.spCounts.set({
          Document: Math.max(0, (spRaw['Document'] ?? spRaw['document'] ?? 0) - dedupedAway('Document')),
          SandboxDocument: Math.max(0, (spRaw['SandboxDocument'] ?? spRaw['sandboxDocument'] ?? 0) - dedupedAway('SandboxDocument')),
        });
        this.park(term);
      })
      .catch((err) => { if (!this.destroyed) console.error('[SharePoint search]', err); }) // errors visible in console
      // Token-guarded: a superseded call resolving late must not clear the current one's flag.
      .finally(() => { if (!this.destroyed && token === this.spToken) this.loadingSharePoint.set(false); });
  }


  private close(): void {
    this.popoverVisible.set(false);
    this.activeIndex.set(-1);
    (document.activeElement as HTMLElement)?.blur();
  }
}
